// Scale bridge dashboard - the receiver's operator API.
//
// ─── Two things worth knowing about the credential ─────────────────────
// 1. It is a SESSION token, issued by /auth/login against the operator's username,
//    weighbridge id and licence key. It is not the device key: the device key can
//    WRITE readings, and putting that in a browser would let anyone with devtools
//    file bills as the weighbridge - while rotating it means a trip to the hardware
//    and the BOOT button.
// 2. It travels in a header, never the query string. SBIS puts its session token
//    in a WebSocket URL (its L-5, still open), which nginx then writes into the
//    access log on every request.
//
// It is held in localStorage, which is readable by any script that gets into this
// origin. That is the same trade SBIS makes and the same one it records as L-6. What
// bounds it here is that the token expires, and that lib/user-state.ts can revoke it
// inside a minute without waiting for that expiry.

import type { Reading, Stats } from '../types';

const STORAGE_KEY = 'scale-bridge.session';

export interface Session {
    /** Receiver base URL. Empty means same origin, which is the packaged case. */
    baseUrl: string;
    /** The operator's session token. The only credential this app carries. */
    token: string;
    /** Who the token belongs to. Display only; the server never trusts this. */
    username?: string;
    accountName?: string;
    /**
     * operator | admin. Taken from the LOGIN RESPONSE, not read back out of storage:
     * SBIS records that reading it from the previous session's cache made a first
     * sign-in always fall through to 'operator', which matters the moment the UI
     * keys admin affordances off it.
     *
     * A display concern only. Every admin route is gated server-side by requireAdmin,
     * so editing this in devtools reveals buttons that then answer 403.
     */
    role?: string;
}

export function loadSession(): Session | null {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as Session;
        // No token, no session. A stored entry without one predates operator
        // accounts and cannot authenticate anything, so it is treated as absent
        // rather than restored into a dashboard that would 401 on its first fetch.
        if (typeof parsed?.token !== 'string' || !parsed.token) return null;
        return {
            baseUrl: parsed.baseUrl ?? '',
            token: parsed.token,
            username: parsed.username,
            accountName: parsed.accountName,
        };
    } catch {
        // A corrupt entry should log the operator out, not white-screen the app.
        return null;
    }
}

export function saveSession(session: Session): void {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
}

export function clearSession(): void {
    localStorage.removeItem(STORAGE_KEY);
}

/** Thrown for a 401, so the UI can send the operator back to the key prompt
 *  instead of showing an empty dashboard that looks like "no data yet". */
export class UnauthorizedError extends Error {
    constructor() {
        super('The view key was rejected.');
        this.name = 'UnauthorizedError';
    }
}

/** The operator's session token. The only credential this app carries. */
export function authHeader(session: Session): Record<string, string> {
    return { Authorization: `Bearer ${session.token}` };
}

/**
 * Provision this shop — the step that CREATES the account.
 *
 * Runs before the first sign-in on a new machine. Without it, activation has nothing
 * to authenticate against: a shop typing a valid WeighBridge ID and License Key for
 * the first time has no account yet, and login can only ever say "not correct".
 *
 * Idempotent. A second computer activating against an already-provisioned licence
 * gets the existing account back, not a new one.
 */
export async function provision(input: {
    baseUrl: string;
    weighbridgeId: string;
    licenseKey: string;
}): Promise<{ accountName: string; alreadyProvisioned: boolean }> {
    const base = input.baseUrl.replace(/\/+$/, '');
    const res = await fetch(`${base}/api/v1/provision`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            weighbridge_id: input.weighbridgeId,
            license_key: input.licenseKey,
        }),
    });
    const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    if (res.ok) {
        return {
            accountName: String((body.account as { name?: string })?.name ?? ''),
            alreadyProvisioned: Boolean(body.already_provisioned),
        };
    }
    // 503 is an OUTAGE, not a refusal - the shop may be perfectly licensed and
    // somebody else's server merely down. Anything else is the authority saying no,
    // and its message is passed through verbatim: it names which of the two fields
    // is wrong, which is the whole reason for surfacing it.
    if (res.status === 503 || res.status >= 500 || res.status === 0) {
        throw new ProvisioningUnreachableError();
    }
    throw new LicenseRejectedError(String(body.message ?? 'That licence was not recognised.'));
}

/** The pair was adjudicated and refused. The operator must check the certificate. */
export class LicenseRejectedError extends Error {
    constructor(message: string) {
        super(message);
        this.name = 'LicenseRejectedError';
    }
}

/**
 * Provisioning could not be reached. NOT fatal: the shop may already be provisioned
 * and simply be activating another computer, in which case the sign-in below will
 * succeed on its own. Refusing here would make a transient outage look like a bad
 * licence.
 */
export class ProvisioningUnreachableError extends Error {
    constructor() {
        super('Could not reach provisioning.');
        this.name = 'ProvisioningUnreachableError';
    }
}

export interface LoginInput {
    baseUrl: string;
    username: string;
    password: string;
    weighbridgeId?: string;
    licenseKey?: string;
}

export interface LoginResult {
    token: string;
    username: string;
    accountName: string;
    role: string;
}

/**
 * Sign in. Distinguishes the three answers the server gives, because they need
 * three different things from the operator:
 *
 *   401  the details were wrong                     -> try again
 *   409  this username exists in several accounts   -> also enter WeighBridge/Key
 *   403  disabled, or the licence expired           -> nothing they can fix here
 */
export async function login(input: LoginInput): Promise<LoginResult> {
    const base = input.baseUrl.replace(/\/+$/, '');
    const res = await fetch(`${base}/api/v1/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            username: input.username,
            password: input.password,
            ...(input.weighbridgeId ? { weighbridge_id: input.weighbridgeId } : {}),
            ...(input.licenseKey ? { license_key: input.licenseKey } : {}),
        }),
    });
    const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    if (res.ok) {
        const user = body.user as { username?: string; role?: string } | undefined;
        return {
            token: String(body.token),
            username: String(user?.username ?? input.username),
            accountName: String((body.account as { name?: string })?.name ?? ''),
            role: String(user?.role ?? 'operator'),
        };
    }
    if (res.status === 409) throw new AccountRequiredError();
    throw new LoginError(String(body.message ?? 'Sign-in failed.'), String(body.error ?? ''));
}

export class LoginError extends Error {
    constructor(message: string, readonly code: string) {
        super(message);
        this.name = 'LoginError';
    }
}

/** The username is ambiguous — the WeighBridge ID and License Key are needed too. */
export class AccountRequiredError extends Error {
    constructor() {
        super('This username exists in more than one account. Enter your WeighBridge ID and License Key as well.');
        this.name = 'AccountRequiredError';
    }
}

async function request<T>(
    session: Session,
    path: string,
    init: RequestInit = {},
    // Defaults to the operator API. The fleet routes live one level up, so the
    // prefix is a parameter rather than a second copy of this function - which is
    // where the auth header and the 401 handling would drift apart.
    prefix = '/api/v1/operator',
): Promise<T> {
    const base = session.baseUrl.replace(/\/+$/, '');
    const res = await fetch(`${base}${prefix}${path}`, {
        ...init,
        headers: { ...(init.headers ?? {}), ...authHeader(session) },
    });

    if (res.status === 401) throw new UnauthorizedError();
    if (!res.ok) {
        let detail = '';
        try {
            detail = ((await res.json()) as { message?: string }).message ?? '';
        } catch {
            /* a non-JSON error body is not worth a second failure */
        }
        throw new Error(detail || `Request failed with ${res.status}`);
    }
    return (await res.json()) as T;
}

export interface FleetDevice {
    id: string;
    deviceId: string;
    name: string | null;
    lastSeen: string | null;
    disabled: boolean;
    readingCount: number;
    createdAt: string;
}

/**
 * A device that has been named and given a code, but has not presented itself yet.
 *
 * There is no Device row behind this: device_id comes from the ESP32's MAC, and
 * nobody knows it until the hardware claims. Note what is NOT on this type - the
 * code. The server keeps only a hash, so there is nothing for it to return.
 */
export interface PendingClaim {
    id: string;
    machineLabel: string;
    location: string | null;
    expiresAt: string;
    createdAt: string;
}

export interface ReleasePreview {
    deviceId: string;
    name: string | null;
    readingCount: number;
    /** What releasing would do to the row. The DATA decides, not the operator. */
    rowFate: 'deleted' | 'retained_as_anchor';
    lastSeen: string | null;
}

/** The fleet. Separate from the operator API because these WRITE. */
export const devices = {
    list: (s: Session) => request<{ devices: FleetDevice[] }>(s, '/devices', {}, '/api/v1'),
    rename: (s: Session, id: string, name: string | null) =>
        request<{ ok: true; name: string | null }>(s, `/devices/${id}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ name }),
        }, '/api/v1'),
    disable: (s: Session, id: string) =>
        request<{ ok: true }>(s, `/devices/${id}`, { method: 'DELETE' }, '/api/v1'),
    restore: (s: Session, id: string) =>
        request<{ ok: true }>(s, `/devices/${id}/restore`, { method: 'POST' }, '/api/v1'),
    releasePreview: (s: Session, id: string) =>
        request<ReleasePreview>(s, `/devices/${id}/release-preview`, {}, '/api/v1'),
    /** Open claims. The code is never among them - see PendingClaim. */
    pending: (s: Session) =>
        request<{ claims: PendingClaim[]; ttlMinutes: number }>(s, '/devices/provision', {}, '/api/v1'),
    /** The ONE call that ever returns a claim code. It is not stored anywhere. */
    provision: (s: Session, machineLabel: string, location: string | null) =>
        request<{ ok: true; claim: PendingClaim; code: string; ttlMinutes: number }>(
            s,
            '/devices/provision',
            {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ machine_label: machineLabel, location }),
            },
            '/api/v1',
        ),
    cancelClaim: (s: Session, id: string) =>
        request<{ ok: true }>(s, `/devices/provision/${id}`, { method: 'DELETE' }, '/api/v1'),
    release: (s: Session, id: string, confirm: string) =>
        request<{ ok: true }>(s, `/devices/${id}/release`, {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ confirm }),
        }, '/api/v1'),
};

export const client = {
    session: (s: Session) => request<{ ok: true; devices: string[] }>(s, '/session'),
    bills: (s: Session, limit = 200) =>
        request<{ count: number; readings: Reading[] }>(s, `/bills?limit=${limit}`),
    bill: (s: Session, id: string) => request<Reading>(s, `/bills/${encodeURIComponent(id)}`),
    stats: (s: Session) => request<Stats>(s, '/stats'),
};

/** The customer-facing bill page, for the "open bill" link. Not part of the
 *  operator API - it is the same page a phone opens. */
export function billUrl(session: Session, id: string): string {
    const base = session.baseUrl.replace(/\/+$/, '');
    return `${base}/b/${encodeURIComponent(id)}`;
}
